import LaunchOutlined from "@mui/icons-material/LaunchOutlined";
import { IconButton, Tooltip, Box } from "@mui/material";
import { isPOBoxAddress } from "encamp-shared/src/utils/address";
import { gql } from "generated-graphql";
import { FireDepartmentPickerFragment } from "generated-graphql/graphql";
import { useValidatingFormContext } from "hooks/useValidatingForm";
import { forwardRef, useMemo } from "react";
import { ControllerFieldState } from "react-hook-form";

import AsyncAutocomplete from "./Forms/AsyncAutocomplete";
import { DisplayOnlyField } from "./Forms/DisplayOnlyField";
import EncampAutocomplete from "./Forms/EncampAutocomplete";
import {
  calculateAgencyDistance,
  formatDistance,
  getFireDepartmentCoordinates,
} from "./LocalAgencies/utils";
import { OmnisearchAutocomplete } from "./OmnisearchAutocomplete";

// Base props shared between both picker variants
type BaseFireDepartmentPickerProps = {
  onChange?: (value: FireDepartmentPickerFragment | null) => void;
  value?: FireDepartmentPickerFragment | null;
  disabled?: boolean;
  helperText?: string | React.JSX.Element;
  displayAgencyLink?: boolean;
  facilityLatitude?: number | null;
  facilityLongitude?: number | null;
} & ControllerFieldState;

// Props specific to state/county based picker
type StateCountyFireDepartmentPickerProps = BaseFireDepartmentPickerProps & {
  facilityState?: string;
  facilityCounty?: string;
};

// Props specific to facility based picker
type FacilityFireDepartmentPickerProps = BaseFireDepartmentPickerProps & {
  facilityIds: string[];
};

// Props specific to omnisearch picker with filtering
type OmnisearchFireDepartmentPickerProps = BaseFireDepartmentPickerProps & {
  defaultSearch?: string;
  resultFilter?: (fireDepartment: FireDepartmentPickerFragment) => boolean;
};

// GraphQL fragments and queries
const FIRE_DEPARTMENT_PICKER_FRAGMENT = gql(`
  fragment FireDepartmentPicker on FireDepartment {
    id
    name
    city
    state
    mailingAddress
    mailingAddressLine2
    zip
    county
    phone
    email
    isMailed
    isEmailed
    isSubmittedViaPortal
    stateSubmitsToFireDept
    needsT2sFile
    preferUncertified
    deactivated
    firstName
    lastName
    latitude
    longitude
  }
`);

const FIRE_DEPARTMENTS_BY_STATE_COUNTY_QUERY = gql(`
  query FireDepartments($state: String, $county: String) {
    fireDepartments(state: $state, county: $county) {
      items {
        ...FireDepartmentPicker
      }
      count
    }
  }
`);

const FIRE_DEPARTMENTS_SEARCH_BY_FACILITY_QUERY = gql(`
  query FireDepartmentSearchForPicker($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    fireDepartmentSearch(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        ...FireDepartmentPicker
      }
      count
    }
  }
`);

// Shared function to format Fire Department option labels
const getFireDepartmentOptionLabel = (fireDepartment: FireDepartmentPickerFragment) => {
  return [fireDepartment.name, fireDepartment.city, fireDepartment.county, fireDepartment.state]
    .filter(Boolean)
    .join(", ");
};

// Function to sort Fire Departments by distance when coordinates are available
const sortFireDepartmentsByDistance = (
  fireDepartments: FireDepartmentPickerFragment[],
  facilityLatitude?: number | null,
  facilityLongitude?: number | null
): FireDepartmentPickerFragment[] => {
  if (!facilityLatitude || !facilityLongitude) {
    return fireDepartments;
  }

  const facilityCoords = {
    latitude: facilityLatitude,
    longitude: facilityLongitude,
  };

  // Calculate distances once for all fire departments
  const fireDepartmentsWithDistances = fireDepartments.map((dept) => ({
    dept,
    distance: calculateAgencyDistance(facilityCoords, getFireDepartmentCoordinates(dept)),
  }));

  return fireDepartmentsWithDistances
    .sort((a, b) => {
      const aDistance = a.distance;
      const bDistance = b.distance;

      // If both have distances, sort by distance
      if (aDistance !== undefined && bDistance !== undefined) {
        return aDistance - bDistance;
      }

      // If only one has distance, prefer the one WITHOUT distance (put at top)
      if (aDistance !== undefined && bDistance === undefined) {
        return 1;
      }
      if (aDistance === undefined && bDistance !== undefined) {
        return -1;
      }

      // If neither has distance, maintain original order
      return 0;
    })
    .map((item) => item.dept);
};

// Custom render option function for displaying distance
const createFireDepartmentRenderOption = (
  facilityLatitude?: number | null,
  facilityLongitude?: number | null,
  displayAgencyLink?: boolean
) => {
  if (!facilityLatitude || !facilityLongitude) {
    if (!displayAgencyLink) return undefined;

    const FireDepartmentOptionWithLink = (
      props: React.HTMLAttributes<HTMLLIElement>,
      option: FireDepartmentPickerFragment
    ) => (
      <li
        {...props}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>{getFireDepartmentOptionLabel(option)}</span>
        {agencyLinkAdornment(option)}
      </li>
    );
    FireDepartmentOptionWithLink.displayName = "FireDepartmentOptionWithLink";
    return FireDepartmentOptionWithLink;
  }

  const facilityCoords = {
    latitude: facilityLatitude,
    longitude: facilityLongitude,
  };

  const FireDepartmentOptionWithDistance = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: FireDepartmentPickerFragment
  ) => {
    const agencyCoords = getFireDepartmentCoordinates(option);
    const distance = calculateAgencyDistance(facilityCoords, agencyCoords);
    const isPoBox = isPOBoxAddress(option.mailingAddress);

    return (
      <li {...props}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            gap: 1,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <span>{getFireDepartmentOptionLabel(option)}</span>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
            }}
          >
            {isPoBox ? (
              <Box
                sx={{
                  fontSize: "0.75rem",
                  color: "text.secondary",
                  whiteSpace: "nowrap",
                }}
              >
                PO Box
              </Box>
            ) : distance !== undefined ? (
              <Box
                sx={{
                  fontSize: "0.75rem",
                  color: "text.secondary",
                  whiteSpace: "nowrap",
                }}
              >
                {formatDistance(distance)}
              </Box>
            ) : null}
            {displayAgencyLink && agencyLinkAdornment(option)}
          </Box>
        </Box>
      </li>
    );
  };
  FireDepartmentOptionWithDistance.displayName = "FireDepartmentOptionWithDistance";
  return FireDepartmentOptionWithDistance;
};

// State/County based picker
// NOTE: Using forwardRef to properly handle ref forwarding and prevent React warnings
// when this component is used with form libraries like react-hook-form
export const FireDepartmentPicker = forwardRef<
  HTMLDivElement,
  StateCountyFireDepartmentPickerProps
>(function FireDepartmentPicker(
  {
    onChange,
    value,
    facilityState,
    facilityCounty,
    error,
    disabled,
    helperText,
    displayAgencyLink,
    facilityLatitude,
    facilityLongitude,
  },
  ref
) {
  // Memoize the sorted items function
  const getSortedItems = useMemo(() => {
    return (data: any) => {
      const items = data.fireDepartments?.items ?? [];
      return sortFireDepartmentsByDistance(items, facilityLatitude, facilityLongitude);
    };
  }, [facilityLatitude, facilityLongitude]);

  // Memoize the render option function
  const renderOptionFunc = useMemo(() => {
    return createFireDepartmentRenderOption(facilityLatitude, facilityLongitude, displayAgencyLink);
  }, [facilityLatitude, facilityLongitude, displayAgencyLink]);

  const formContext = useValidatingFormContext();
  if (formContext?.metadata?.isDisplayOnly) {
    return (
      <DisplayOnlyField text={value ? getFireDepartmentOptionLabel(value) : ""} label={"FD"} />
    );
  }

  return (
    <EncampAutocomplete
      query={FIRE_DEPARTMENTS_BY_STATE_COUNTY_QUERY}
      variables={{
        state: facilityState,
        county: facilityCounty,
      }}
      getItems={getSortedItems}
      getOptionLabel={getFireDepartmentOptionLabel}
      onChange={onChange}
      value={value || null}
      error={error}
      disabled={disabled}
      helperText={helperText}
      additionalEndAdornment={displayAgencyLink && agencyLinkAdornment(value)}
      renderOption={renderOptionFunc}
      label="Fire District"
    />
  );
});

// Facility based picker
export const FireDepartmentPickerWithFacilityFilter = forwardRef<
  HTMLDivElement,
  FacilityFireDepartmentPickerProps
>(function FireDepartmentPickerWithFacilityFilter(
  {
    facilityIds,
    onChange,
    value,
    error,
    disabled,
    helperText,
    displayAgencyLink,
    facilityLatitude,
    facilityLongitude,
  },
  ref
) {
  // Memoize the sorted items function
  const getSortedItems = useMemo(() => {
    return (data: any) => {
      const items = data.fireDepartmentSearch?.items ?? [];
      return sortFireDepartmentsByDistance(items, facilityLatitude, facilityLongitude);
    };
  }, [facilityLatitude, facilityLongitude]);

  // Memoize the render option function
  const renderOptionFunc = useMemo(() => {
    return createFireDepartmentRenderOption(facilityLatitude, facilityLongitude, displayAgencyLink);
  }, [facilityLatitude, facilityLongitude, displayAgencyLink]);

  // Compose facility filter for search
  const facilityFilter = facilityIds.length ? `facilityId:${facilityIds.join("|")}` : "";

  return (
    <AsyncAutocomplete
      query={FIRE_DEPARTMENTS_SEARCH_BY_FACILITY_QUERY}
      getItems={getSortedItems}
      getOptionLabel={getFireDepartmentOptionLabel}
      onChange={onChange}
      value={value || null}
      error={error}
      disabled={disabled}
      additionalSearchString={facilityFilter}
      helperText={helperText}
      additionalEndAdornment={displayAgencyLink && agencyLinkAdornment(value)}
      renderOption={renderOptionFunc}
      placeholderLabel="Fire District"
    />
  );
});

// Generic omnisearch-based picker with filtering
export const FireDepartmentPickerOmnisearch = forwardRef<
  HTMLDivElement,
  OmnisearchFireDepartmentPickerProps
>(function FireDepartmentPickerOmnisearch(
  {
    defaultSearch,
    resultFilter,
    onChange,
    value,
    error,
    disabled,
    helperText,
    displayAgencyLink,
    facilityLatitude,
    facilityLongitude,
  },
  ref
) {
  // Memoize the sorted items function
  const getSortedItems = useMemo(() => {
    return (data: any) => {
      const items = data.fireDepartmentSearch?.items ?? [];
      return sortFireDepartmentsByDistance(items, facilityLatitude, facilityLongitude);
    };
  }, [facilityLatitude, facilityLongitude]);

  // Memoize the render option function
  const renderOptionFunc = useMemo(() => {
    return createFireDepartmentRenderOption(facilityLatitude, facilityLongitude, displayAgencyLink);
  }, [facilityLatitude, facilityLongitude, displayAgencyLink]);

  const formContext = useValidatingFormContext();
  if (formContext?.metadata?.isDisplayOnly) {
    return (
      <DisplayOnlyField text={value ? getFireDepartmentOptionLabel(value) : ""} label={"FD"} />
    );
  }

  return (
    <OmnisearchAutocomplete
      dataQuery={FIRE_DEPARTMENTS_SEARCH_BY_FACILITY_QUERY}
      getItems={getSortedItems}
      getOptionLabel={getFireDepartmentOptionLabel}
      onChange={onChange}
      value={value || null}
      error={error}
      disabled={disabled}
      helperText={helperText}
      defaultSearch={defaultSearch}
      resultFilter={resultFilter}
      placeholder="Fire District"
      runInitialSearch={true}
      renderOption={renderOptionFunc}
    />
  );
});

function agencyLinkAdornment(value: FireDepartmentPickerFragment | null | undefined) {
  return (
    value && (
      <Tooltip title="Open Fire Department">
        <IconButton
          size="small"
          sx={{
            padding: "4px",
            color: "primary.main",
          }}
          onClick={(e) => {
            e.stopPropagation(); // Prevent triggering the dropdown
            window.open(`/staff/agencies/fireDepartments?omnisearch=id:${value.id}`, "_blank");
          }}
        >
          <LaunchOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
    )
  );
}
